/* eslint-disable @typescript-eslint/no-explicit-any */

import { GAME_CONSTANTS } from './gameConstants';
import type { GameDate } from '../types/events';

// Central configuration file for all game formulas and calculations
// This file contains all the mathematical formulas used throughout the game

export const GAME_FORMULAS = {
  // Market & Sales Formulas
  market: {
    // Calculate market value based on market share
    getMarketValue: (marketShare: number) => 
      marketShare * GAME_CONSTANTS.finance.baseRevenuePerMarketShare,
    
    // Calculate operational costs from revenue
    getOperationalCost: (revenue: number) => 
      revenue * GAME_CONSTANTS.finance.operationalCostsRatio,
    
    // Calculate daily operating costs
    getDailyOperatingCost: (baseOperatingCost: number, activeProductCount: number) =>
      baseOperatingCost + Math.sqrt(activeProductCount) * GAME_CONSTANTS.finance.overheadCostMultiplier,
    
    // Price multiplier using logistics curve
    getPriceMultiplier: (price: number, optimalPrice: number, k: number = 5) => {
      const priceRatio = (price - optimalPrice) / optimalPrice;
      return 1 / (1 + Math.exp(k * priceRatio));
    },
    
    // Quality multiplier based on tech score, market appeal, and user score
    getQualityMultiplier: (techScore: number, marketAppeal: number, userScore: number) =>
      (techScore * GAME_CONSTANTS.sales.qualityWeights.tech + 
       marketAppeal * GAME_CONSTANTS.sales.qualityWeights.appeal + 
       userScore * GAME_CONSTANTS.sales.qualityWeights.userScore) / 100,
    
    // Competition multiplier
    getCompetitionMultiplier: (baseMultiplier: number, competitionIntensity: number) =>
      baseMultiplier * (1.5 - competitionIntensity * 0.5),
    
    // Technology adoption using Bass diffusion model
    getTechnologyAdoption: (p: number, q: number, installedBase: number, marketPotential: number) => {
      const remainingPotential = marketPotential - installedBase;
      const innovators = p * remainingPotential;
      const imitators = q * (installedBase / marketPotential) * remainingPotential;
      return innovators + imitators;
    },
    
    // Calculate adoption curve multiplier
    getAdoptionMultiplier: (monthsSinceLaunch: number) => {
      const rate = GAME_CONSTANTS.sales.adoptionRate;
      const midpoint = GAME_CONSTANTS.sales.adoptionMidpoint;
      return 1 / (1 + Math.exp(-rate * (monthsSinceLaunch - midpoint)));
    },
    
    // Get seasonal factor by quarter
    getSeasonalFactor: (month: number) => {
      const quarter = Math.floor((month - 1) / 3);
      return GAME_CONSTANTS.sales.seasonalFactors[quarter] || 1.0;
    },
    
    // Get lifecycle multiplier
    getLifecycleMultiplier: (stage: 'introduction' | 'growth' | 'maturity' | 'decline') =>
      GAME_CONSTANTS.sales.lifecycleMultipliers[stage] || 1.0,
    
    // Calculate segment market size
    getSegmentSize: (segment: 'budget' | 'mainstream' | 'premium' | 'innovative') =>
      GAME_CONSTANTS.marketSegments.sizes[segment] || 0,
    
    // Get price elasticity for segment
    getPriceElasticity: (segment: 'budget' | 'mainstream' | 'premium' | 'innovative') =>
      GAME_CONSTANTS.marketSegments.priceElasticity[segment] || 1.0,
    
    // Calculate economic multiplier
    getEconomicMultiplier: (indicators: { gdp: number, confidence: number, unemployment: number, inflation: number }) => {
      const weights = GAME_CONSTANTS.economic.indicatorWeights;
      const weighted = (indicators.gdp * weights.gdp + 
                       indicators.confidence * weights.confidence + 
                       indicators.unemployment * weights.unemployment + 
                       indicators.inflation * weights.inflation);
      const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
      return weighted / totalWeight;
    }
  },

  // Production & Manufacturing Formulas
  production: {
    // Calculate production output based on employee allocation
    // Used by factoryProductionService and FactoryView for consistent calculations
    calculateProductionOutput: (
      baseCapacity: number,
      allocatedEmployees: number,
      optimalEmployees: number
    ): { efficiency: number; dailyOutput: number } => {
      if (allocatedEmployees === 0 || optimalEmployees === 0) {
        return { efficiency: 0, dailyOutput: 0 };
      }
      const employeeRatio = Math.min(1, allocatedEmployees / optimalEmployees);
      // Slight bonus for overstaffing (up to 20% bonus when at 200% staffing)
      const employeeEfficiency = employeeRatio * (1 + Math.min(0.2, (employeeRatio - 1) * 0.1));
      return {
        efficiency: Math.round(employeeEfficiency * 100),
        dailyOutput: Math.round(baseCapacity * employeeEfficiency)
      };
    },

    // Helper to calculate production output from building type properties
    // Extracts base capacity and optimal employees from building type, then calculates output
    calculateLineProductionFromBuilding: (
      productionCapacity: number | undefined,
      factoryWorkers: number | undefined,
      productionLines: number | undefined,
      allocatedEmployees: number
    ): { efficiency: number; dailyOutput: number } => {
      const baseCapacity = productionCapacity || 100;
      const optimalEmployees = Math.floor(
        (factoryWorkers || 10) / (productionLines || 1)
      );
      return GAME_FORMULAS.production.calculateProductionOutput(
        baseCapacity,
        allocatedEmployees,
        optimalEmployees
      );
    },

    // Calculate labor cost with inefficiency penalty
    getLaborCost: (baseLaborCost: number, efficiency: number) =>
      baseLaborCost * (1 + (1 - efficiency) * GAME_CONSTANTS.production.inefficiencyPenalty),
    
    // Calculate overhead cost based on completion ratio
    getOverheadCost: (baseOverheadCost: number, completedUnits: number, targetUnits: number) =>
      baseOverheadCost * (completedUnits / targetUnits),
    
    // Calculate overall efficiency
    getEfficiency: (quantityEfficiency: number, qualityEfficiency: number) =>
      (quantityEfficiency * 0.6 + qualityEfficiency * 0.4) * 100,
    
    // Calculate total production cost
    getProductionCost: (componentsCost: number) => {
      const overhead = componentsCost * GAME_CONSTANTS.production.manufacturingOverhead;
      const assemblyCost = componentsCost * GAME_CONSTANTS.production.assemblyCost;
      const qualityControlCost = componentsCost * GAME_CONSTANTS.production.qualityControlCost;
      return componentsCost + overhead + assemblyCost + qualityControlCost;
    },
    
    // Calculate defect rate based on tech score
    getDefectRate: (techScore: number) => {
      const reduction = techScore * GAME_CONSTANTS.quality.techScoreDefectReduction;
      const baseRate = GAME_CONSTANTS.quality.baseDefectRate - reduction;
      return Math.max(
        GAME_CONSTANTS.quality.minDefectRate,
        Math.min(GAME_CONSTANTS.quality.maxDefectRate, baseRate)
      );
    },
    
    // Calculate defect rate with variation
    getDefectRateWithVariation: (baseDefectRate: number) => {
      const variation = (Math.random() - 0.5) * 2 * GAME_CONSTANTS.quality.defectRateVariation;
      return Math.max(
        GAME_CONSTANTS.quality.minDefectRate,
        Math.min(GAME_CONSTANTS.quality.maxDefectRate, baseDefectRate + variation)
      );
    },
    
    // Calculate manufacturing time for products
    getManufacturingTime: (baseTime: number = 30, productComplexity: number = 1, efficiencyBonus: number = 0) => {
      // Base time is 30 days (1 month), adjustable by complexity and efficiency
      const complexityMultiplier = 0.5 + (productComplexity / 100) * 0.5; // 0.5x to 1x based on tech score
      const timeInDays = baseTime * complexityMultiplier * (1 - efficiencyBonus);
      return Math.max(7, Math.round(timeInDays)); // Minimum 7 days
    }
  },

  // Employee & Operations Formulas
  employee: {
    // Calculate severance cost
    getSeveranceCost: (monthlySalary: number) =>
      monthlySalary * GAME_CONSTANTS.employee.severanceMultiplier,
    
    // Calculate reputation impact from firing
    getReputationImpact: (employeeCount: number) => {
      const impact = employeeCount * GAME_CONSTANTS.employee.reputationLossPerFiring;
      return Math.min(impact, GAME_CONSTANTS.employee.maxReputationLossPerAction);
    },
    
    // Calculate total daily salary costs
    getDailySalaryCosts: (employees: Array<{ salary: number }>) =>
      employees.reduce((total, employee) => total + employee.salary, 0),
    
    // Calculate employee performance
    getEmployeePerformance: (basePerformance: number, happiness: number, experience: number) =>
      basePerformance * (0.5 + happiness / 200) * (1 + experience / 100),
    
    // Calculate team productivity multiplier
    getProductivityMultiplier: (stats: any, employees: Array<{ performance: number, happiness: number, skills: any, role: string }>) => {
      if (stats.total === 0) return GAME_CONSTANTS.employee.noEmployeePenalty;
      
      // Performance factor
      const avgPerformance = employees.reduce((sum, emp) => sum + emp.performance, 0) / employees.length;
      const performanceFactor = GAME_CONSTANTS.employee.performanceWeight + 
        (avgPerformance / 100) * GAME_CONSTANTS.employee.performanceWeight;
      
      // Happiness factor
      const avgHappiness = employees.reduce((sum, emp) => sum + emp.happiness, 0) / employees.length;
      const happinessFactor = GAME_CONSTANTS.employee.happinessWeight + 
        (avgHappiness / 100) * GAME_CONSTANTS.employee.skillWeight;
      
      // Team composition factor
      const totalRatio = Object.values(GAME_CONSTANTS.employee.idealTeamRatio).reduce((sum: number, val) => sum + val, 0);
      let deviationSum = 0;
      Object.entries(GAME_CONSTANTS.employee.idealTeamRatio).forEach(([role, ideal]) => {
        const actual = (stats[role] || 0) / stats.total;
        const expected = ideal / totalRatio;
        deviationSum += Math.abs(actual - expected);
      });
      const compositionFactor = Math.max(0, 1 - deviationSum * GAME_CONSTANTS.employee.deviationPenalty);
      
      // Scale bonus
      const scaleBonus = 1 + Math.log10(stats.total + 1) * 0.2;
      
      // Final multiplier
      const multiplier = performanceFactor * happinessFactor * compositionFactor * scaleBonus;
      return Math.max(
        GAME_CONSTANTS.employee.productivityClampMin, 
        Math.min(GAME_CONSTANTS.employee.productivityClampMax, multiplier)
      );
    },
    
    // Get daily performance change
    getDailyPerformanceChange: () => 
      (Math.random() - 0.5) * GAME_CONSTANTS.employee.dailyPerformanceVariation,
    
    // Get daily happiness change
    getDailyHappinessChange: () =>
      (Math.random() - 0.5) * GAME_CONSTANTS.employee.dailyHappinessVariation,
    
    // Convert annual salary to daily
    getAnnualToDailySalary: (annualSalary: number) =>
      Math.round(annualSalary / GAME_CONSTANTS.time.daysPerYear),
    
    // Calculate employee requirements for a development stage
    getStageEmployeeRequirement: (techScore: number, currentYear: number, stage: 'design' | 'engineering' | 'testing' | 'manufacturing' | 'production') => {
      // Get the specific base requirement for this stage and role
      const employeeType = GAME_CONSTANTS.employee.stageRequirements[stage];
      if (!employeeType) {
        console.warn(`Unknown stage: ${stage}, defaulting to engineer`);
        return GAME_CONSTANTS.employee.employeesPerDevelopmentStage;
      }
      
      const minRequirements = GAME_CONSTANTS.employee.minEmployeesPerPhase[stage];
      if (!minRequirements || !minRequirements[employeeType]) {
        console.warn(`No requirements found for stage: ${stage}, role: ${employeeType}`);
        return GAME_CONSTANTS.employee.employeesPerDevelopmentStage;
      }
      
      const baseRequirement = minRequirements[employeeType];
      const scaling = GAME_CONSTANTS.employee.employeeScaling;
      
      // Tech complexity multiplier (1.0 + 0.01 per tech score point)
      const techMultiplier = Math.min(
        scaling.maxTechMultiplier,
        scaling.techComplexityBase + (techScore * scaling.techComplexityFactor)
      );
      
      // Time progression multiplier (1.0 + 2% per year after 2000)
      const yearsSince2000 = Math.max(0, currentYear - 2000);
      const timeMultiplier = Math.min(
        scaling.maxTimeMultiplier,
        scaling.timeProgressionBase + (yearsSince2000 * scaling.timeProgressionFactor)
      );
      
      // Calculate final requirement (round up to ensure minimum staffing)
      return Math.ceil(baseRequirement * techMultiplier * timeMultiplier);
    },
    
    // Get the required employee type for a development stage
    getStageEmployeeType: (stage: 'design' | 'engineering' | 'testing' | 'manufacturing' | 'production') =>
      GAME_CONSTANTS.employee.stageRequirements[stage] || 'engineer'
  },

  // Research & Development Formulas
  research: {
    // Calculate daily RP generation from investment
    getInvestmentRP: (monthlyInvestment: number, rpGenerationRate: number) =>
      (monthlyInvestment * GAME_CONSTANTS.research.baseRPRate / 30) * rpGenerationRate,
    
    // Calculate product-based RP generation
    getProductRP: (reputation: number, productCount: number) =>
      productCount > 0 ? Math.floor((reputation / 10) * productCount) : 0,
    
    // Calculate researcher-based RP generation
    getResearcherRP: (researcherCount: number, avgPerformance: number, monthlyInvestment: number) => {
      const baseRP = researcherCount * 2; // 2 RP per researcher per day
      const performanceBonus = avgPerformance / 100;
      const investmentBonus = 1 + Math.log10(1 + monthlyInvestment / 100000);
      return Math.floor(baseRP * performanceBonus * investmentBonus);
    },
    
    // Calculate total daily RP
    getTotalDailyRP: (investmentRP: number, productRP: number, researcherRP: number) =>
      investmentRP + productRP + researcherRP,
    
    // Calculate research duration with bonuses
    getResearchDuration: (baseDuration: number, efficiencyBonus: number) =>
      Math.ceil(baseDuration * (1 - efficiencyBonus))
  },

  // Product Metrics Formulas
  productMetrics: {
    // Calculate tech score impact
    getTechScoreImpact: (techScore: number) =>
      techScore * GAME_CONSTANTS.productMetrics.techScoreWeight,
    
    // Calculate user satisfaction
    getUserSatisfaction: (techScore: number, valueForMoney: number) => {
      const techWeight = GAME_CONSTANTS.productMetrics.userSatisfactionTechWeight;
      const valueWeight = GAME_CONSTANTS.productMetrics.userSatisfactionValueWeight;
      return techScore * techWeight + valueForMoney * valueWeight;
    },
    
    // Calculate value for money
    getValueForMoney: (price: number, techScore: number, referencePrice: number = GAME_CONSTANTS.productMetrics.basePriceReference) => {
      const expectedPrice = referencePrice * (techScore / 50);
      return Math.max(0, Math.min(100, 100 * (expectedPrice / price)));
    },
    
    // Calculate market appeal
    getMarketAppeal: (designScore: number, brandStrength: number, marketingEffectiveness: number) =>
      designScore * 0.4 + brandStrength * 0.3 + marketingEffectiveness * 0.3,
    
    // Calculate overall product score
    getProductScore: (techScore: number, designScore: number, pricePoint: number) => {
      const techImpact = techScore * GAME_CONSTANTS.productMetrics.techScoreWeight;
      const priceImpact = (100 - pricePoint) * GAME_CONSTANTS.productMetrics.priceRatioWeight;
      return techImpact + designScore * 0.2 + priceImpact;
    }
  },

  // Financial Formulas
  finance: {
    // Calculate profit margin
    getProfitMargin: (revenue: number, costs: number) =>
      revenue > 0 ? ((revenue - costs) / revenue) * 100 : 0,
    
    // Calculate ROI
    getROI: (revenue: number, investment: number) =>
      investment > 0 ? ((revenue - investment) / investment) * 100 : 0,
    
    // Calculate break-even point
    getBreakEvenUnits: (fixedCosts: number, pricePerUnit: number, variableCostPerUnit: number) => {
      const contributionMargin = pricePerUnit - variableCostPerUnit;
      return contributionMargin > 0 ? Math.ceil(fixedCosts / contributionMargin) : Infinity;
    },
    
    // Calculate cash flow
    getCashFlow: (revenue: number, operatingCosts: number, capitalExpenses: number = 0) =>
      revenue - operatingCosts - capitalExpenses,
    
    // Check bankruptcy status
    isBankrupt: (cash: number) =>
      cash < GAME_CONSTANTS.finance.bankruptcyThreshold
  },

  // Reputation Formulas
  reputation: {
    // Calculate reputation growth from product rating
    getReputationGrowth: (rating: number) => {
      if (rating >= 4.0) return GAME_CONSTANTS.reputation.gainPerSuccessfulProduct;
      if (rating < 3.0 && rating > 0) return -GAME_CONSTANTS.reputation.lossPerPoorProduct;
      return 0;
    },

    // Calculate reputation decay
    getReputationDecay: (currentReputation: number) =>
      currentReputation * GAME_CONSTANTS.reputation.decayRate,

    // Calculate reputation impact on sales
    getReputationMultiplier: (reputation: number) =>
      1 + (reputation / 100) * GAME_CONSTANTS.market.reputationSalesMultiplier,

    // Calculate reputation gain from sales volume
    calculateReputationFromSales: (dailySales: number, marketShare: number) => {
      // Base reputation from sales volume
      const baseSalesReputation = dailySales * GAME_CONSTANTS.reputation.salesReputationGainRate;

      // Market share bonus
      const marketShareBonus = marketShare * GAME_CONSTANTS.reputation.marketShareReputationMultiplier;

      // Apply market share tier multiplier
      let tierMultiplier = 1.0;
      const tiers = GAME_CONSTANTS.reputation.marketShareTiers;

      if (marketShare >= tiers.dominant.threshold * 0.5) {
        tierMultiplier = tiers.dominant.multiplier;
      } else if (marketShare >= tiers.large.threshold * 0.25) {
        tierMultiplier = tiers.large.multiplier;
      } else if (marketShare >= tiers.moderate.threshold * 0.1) {
        tierMultiplier = tiers.moderate.multiplier;
      } else if (marketShare >= tiers.small.threshold * 0.05) {
        tierMultiplier = tiers.small.multiplier;
      }

      // Calculate total with cap
      const totalGain = (baseSalesReputation + marketShareBonus) * tierMultiplier;
      return Math.min(totalGain, GAME_CONSTANTS.reputation.reputationGrowthCap);
    },

    // Calculate reputation growth rate based on current reputation and performance
    calculateReputationGrowthRate: (currentRep: number, dailySales: number, marketShare: number) => {
      // Base growth from sales
      const salesGrowth = GAME_FORMULAS.reputation.calculateReputationFromSales(dailySales, marketShare);

      // Diminishing returns at higher reputation levels
      const diminishingFactor = Math.max(0.1, 1 - (currentRep / 200));

      return salesGrowth * diminishingFactor;
    },

    // Calculate market share bonus for reputation
    calculateMarketShareBonus: (marketShare: number) => {
      // Progressive bonus based on market dominance
      if (marketShare < 0.05) return 0;
      if (marketShare < 0.10) return 0.1;
      if (marketShare < 0.25) return 0.25;
      if (marketShare < 0.50) return 0.5;
      return 1.0; // Dominant market position
    },

    // Calculate sales volume tier bonus
    calculateSalesVolumeBonus: (totalDailySales: number) => {
      const thresholds = GAME_CONSTANTS.reputation.salesVolumeThresholds;

      if (totalDailySales >= thresholds.high) return 2.0;
      if (totalDailySales >= thresholds.medium) return 1.5;
      if (totalDailySales >= thresholds.low) return 1.0;
      return 0.5; // Below minimum threshold
    }
  },

  // Inventory Management Formulas
  inventory: {
    // Calculate optimal order quantity (Economic Order Quantity)
    getOptimalOrderQuantity: (annualDemand: number, orderingCost: number, holdingCostPerUnit: number) => {
      const eoq = Math.sqrt((2 * annualDemand * orderingCost) / holdingCostPerUnit);
      return Math.round(eoq);
    },
    
    // Calculate reorder point
    getReorderPoint: (dailyDemand: number, leadTimeDays: number, safetyStock: number) =>
      Math.round((dailyDemand * leadTimeDays) + safetyStock),
    
    // Calculate safety stock
    getSafetyStock: (dailyDemand: number, leadTimeDays: number, serviceLevelFactor: number = 1.5) =>
      Math.round(dailyDemand * leadTimeDays * serviceLevelFactor),
    
    // Calculate stockout risk percentage
    getStockoutRisk: (currentStock: number, monthlyConsumption: number) => {
      if (monthlyConsumption === 0) return 0;
      const monthsOfStock = currentStock / monthlyConsumption;
      
      if (monthsOfStock >= 3) return 0;
      if (monthsOfStock >= 2) return 25;
      if (monthsOfStock >= 1) return 50;
      return 100;
    },
    
    // Calculate holding cost
    getHoldingCost: (inventoryValue: number, holdingCostRate: number = GAME_CONSTANTS.inventory.components.storageCostRate) =>
      inventoryValue * holdingCostRate,
    
    // Calculate component consumption for production
    getComponentConsumption: (productionRate: number, unitsPerComponent: number = 1) =>
      productionRate * unitsPerComponent,
    
    // Calculate production rate based on market appeal
    getProductionRate: (baseRate: number, marketAppeal: number) => {
      const appealModifier = marketAppeal / 100;
      return Math.floor(baseRate * appealModifier);
    },
    
    // Calculate order cost with bulk discount
    getOrderCost: (quantity: number, unitCost: number, bulkDiscounts: Record<number, number>) => {
      let discount = 0;
      const sortedQuantities = Object.keys(bulkDiscounts)
        .map(Number)
        .sort((a, b) => b - a);
      
      for (const threshold of sortedQuantities) {
        if (quantity >= threshold) {
          discount = bulkDiscounts[threshold];
          break;
        }
      }
      
      const subtotal = quantity * unitCost;
      return subtotal * (1 - discount);
    },
    
    // Calculate inventory turnover rate
    getInventoryTurnover: (annualCost: number, averageInventoryValue: number) =>
      averageInventoryValue > 0 ? annualCost / averageInventoryValue : 0,
    
    // Calculate days of inventory on hand
    getDaysOfInventory: (currentStock: number, dailyConsumption: number) =>
      dailyConsumption > 0 ? currentStock / dailyConsumption : Infinity
  },

  // Time & Date Formulas
  time: {
    // Convert a GameDate to total days since a base year (for duration calculations)
    gameDateToDays: (date: GameDate, baseYear: number = GAME_CONSTANTS.time.startingDate.year): number => {
      const yearDays = (date.year - baseYear) * GAME_CONSTANTS.time.daysPerYear;
      const monthDays = (date.month - 1) * GAME_CONSTANTS.time.daysPerMonth;
      return yearDays + monthDays + date.day;
    },

    // Calculate days between two GameDates
    getDaysBetween: (startDate: GameDate, endDate: GameDate): number => {
      const startDays = GAME_FORMULAS.time.gameDateToDays(startDate);
      const endDays = GAME_FORMULAS.time.gameDateToDays(endDate);
      return Math.max(0, endDays - startDays);
    },

    // Convert total days to years (approximate)
    daysToYears: (days: number): number => {
      return Math.floor(days / GAME_CONSTANTS.time.daysPerYear);
    },

    // Format a duration in days as a human-readable string
    formatDuration: (days: number): string => {
      const years = Math.floor(days / GAME_CONSTANTS.time.daysPerYear);
      const remainingDays = days % GAME_CONSTANTS.time.daysPerYear;
      const months = Math.floor(remainingDays / GAME_CONSTANTS.time.daysPerMonth);
      const daysLeft = remainingDays % GAME_CONSTANTS.time.daysPerMonth;

      const parts: string[] = [];
      if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
      if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
      if (daysLeft > 0) parts.push(`${daysLeft} day${daysLeft > 1 ? 's' : ''}`);

      return parts.join(', ') || '0 days';
    }
  }
};

// Type-safe getter functions
export function getFormula<T extends keyof typeof GAME_FORMULAS>(
  category: T
): typeof GAME_FORMULAS[T] {
  return GAME_FORMULAS[category];
}

// Export individual categories for convenience
export const MARKET_FORMULAS = GAME_FORMULAS.market;
export const PRODUCTION_FORMULAS = GAME_FORMULAS.production;
export const EMPLOYEE_FORMULAS = GAME_FORMULAS.employee;
export const RESEARCH_FORMULAS = GAME_FORMULAS.research;
export const PRODUCT_FORMULAS = GAME_FORMULAS.productMetrics;
export const FINANCE_FORMULAS = GAME_FORMULAS.finance;
export const REPUTATION_FORMULAS = GAME_FORMULAS.reputation;
export const INVENTORY_FORMULAS = GAME_FORMULAS.inventory;
export const TIME_FORMULAS = GAME_FORMULAS.time;